import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import {
  CheckCircleIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useMutation } from "@tanstack/react-query";
import classNames from "classnames";
import { graphql } from "gatsby";
import { navigate } from "gatsby-link";
import React, { CSSProperties } from "react";
import { Controller, useForm } from "react-hook-form";
import PhoneInput from "react-phone-number-input/input";
import useFormValuesFromURL, {
  AllowedSearchParams,
} from "../hooks/useFormValuesFromURL";
import { Source } from "../hooks/useJobsQuery";
import { trackCustomEvent, trackCustomException } from "../utils/analytics";
import coReg, { RegisterInputProps } from "../utils/coReg";
import getCountryCodeFromCountry from "../utils/getCountryCodeFromCountry";
import { ButtonSecondary } from "./forms/Buttons";
import InputWithLabel from "./forms/InputWithLabel";
import { JobLinkProps } from "./JobLink";
import JornayaScript from "./JornayaScript";

const COUNTRY_CODE = getCountryCodeFromCountry();

export default function HostedApply({
  job,
  gaCategory,
  cpaModalDisclosure,
  hostedApplyTx,
  submit,
}: {
  job: JobLinkProps["job"];
  cpaModalDisclosure: ReturnType<typeof useDisclosure>;
  gaCategory: JobLinkProps["gaCategory"];
  submit?: () => void;
  hostedApplyTx: Queries.HostedApplyFragment;
}) {
  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    formState: { defaultValues, errors, touchedFields },
  } = useForm<RegisterInputProps>({ mode: "onBlur" });
  const isFormHydrating = !defaultValues;

  useFormValuesFromURL(window.location, (searchParams: AllowedSearchParams) => {
    reset(searchParams);
  });

  const appcastMutation = useMutation(
    async () => {
      // @todo this should be set in jobs response
      const appcastUrl = `https://apply.appcast.io/api/integration/{integration_id}/apply_form?jid={job_id}`;
      return fetch(appcastUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          candidate_info: {
            name: "",
            fullname: "",
            contact_info: {
              email: "",
              phone: "",
            },
          },
          resume: {},
          back_url: "/serp/thanks", // Partner’s Thank You page. Appcast will redirect user to the URL after the application is completed
          id: "", // Internal tracking id. Needed for troubleshooting. In case it is not provided, we might not support debugging requests for requests happened 2 weeks ago.
        }),
      }).then((res) => res.json());
    },
    {
      onSuccess: async (data) => {
        navigate(data.url);
      },
    }
  );

  const coRegMutation = useMutation(coReg, {
    onSuccess: async () => {
      trackCustomEvent({
        category: gaCategory,
        action: `Data opt-in`,
        label: `[Hosted Apply] ${job.id}`,
      });
      appcastMutation.mutate();
    },
    onError: (error: Error, data) => {
      trackCustomEvent({
        category: gaCategory,
        action: `Data opt-in error`,
        label: `[HostedModal] [${error.message}]`,
      });
      trackCustomException(error.message, false);
      try {
        const errors = JSON.parse(error.message);
        if (errors.includes("INVALID_EMAIL")) {
          setError("email", { type: "validate" });
        }
        if (errors.includes("INVALID_PHONE")) {
          setError("phone", { type: "validate" });
        }
        if (errors.length === 1 && errors[0] === "INVALID_PHONE") {
          trackCustomEvent({
            category: gaCategory,
            action: `Data opt-in error: invalid phone, but ignoring`,
            label: `[HostedModal] [${error.message}]`,
          });
        }
      } catch (err) {
        trackCustomException(err.message, false);
      }
    },
  });

  function onSubmit(data: RegisterInputProps, event) {
    event.stopPropagation(); // <form>s on Path are unexpectedly submitted by this!
    const source = gaCategory === "SERP" ? Source.serp : Source.path;
    return coRegMutation.mutate({ ...data, source });
  }

  const error = coRegMutation.error || appcastMutation.error;

  return (
    <Modal
      isOpen={cpaModalDisclosure.isOpen}
      onClose={cpaModalDisclosure.onClose}
    >
      <ModalOverlay />
      <ModalContent className="mx-4 border border-white" aria-details="">
        <ModalHeader className="flex flex-col gap-2 border-b">
          {hostedApplyTx.title}
        </ModalHeader>
        <ModalCloseButton
          onClick={() => {
            trackCustomEvent({
              category: gaCategory,
              action: "[Hosted Apply] Job modal closed",
              label: job.url,
            });
            if (typeof submit === "function") {
              submit();
            }
          }}
        />
        <ModalBody>
          <form
            onSubmit={handleSubmit(onSubmit)}
            method="get"
            className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8 mt-4"
          >
            <InputWithLabel
              name="firstname"
              type="text"
              touchedFields={touchedFields}
              errors={errors}
              register={register}
              tx={{
                required: hostedApplyTx.required,
              }}
              placeholder={hostedApplyTx.firstName}
              label={hostedApplyTx.firstName}
              invalidText={hostedApplyTx.firstNameInvalid}
              Icon={UserIcon}
              required
              disabled={
                isFormHydrating ||
                coRegMutation.isLoading ||
                appcastMutation.isLoading
              }
            />
            <InputWithLabel
              name="lastname"
              type="text"
              touchedFields={touchedFields}
              errors={errors}
              register={register}
              tx={{
                required: hostedApplyTx.required,
              }}
              placeholder={hostedApplyTx.lastName!}
              label={hostedApplyTx.lastName}
              Icon={UserIcon}
              required
              disabled={
                isFormHydrating ||
                coRegMutation.isLoading ||
                appcastMutation.isLoading
              }
            />
            <InputWithLabel
              name="email"
              type="email"
              touchedFields={touchedFields}
              errors={errors}
              register={register}
              tx={{
                required: hostedApplyTx.required,
              }}
              placeholder={hostedApplyTx.emailPlaceholder}
              label={hostedApplyTx.emailLabel}
              invalidText={hostedApplyTx.emailInvalid}
              Icon={EnvelopeIcon}
              required
              disabled={
                isFormHydrating ||
                coRegMutation.isLoading ||
                appcastMutation.isLoading
              }
            />
            <div className="sm:col-span-2">
              <label
                htmlFor="phone"
                className={"block text-sm font-medium text-gray-700"}
              >
                {hostedApplyTx.phoneNumberLabel}
              </label>
              <div className="relative mt-1 rounded-md shadow-sm">
                <Controller
                  name="phone"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <PhoneInput
                        value={value}
                        onChange={onChange}
                        country={COUNTRY_CODE}
                        id="phone"
                        errors={errors}
                        withCountryCallingCode={false}
                        autoComplete="tel"
                        className={classNames(
                          "block w-full rounded-md border-gray-300 px-4 py-3 focus:border-primary-600 focus:ring-primary-600",
                          {
                            "border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500":
                              errors.phone,
                          },
                          {
                            "border-green-500 text-green-500 placeholder-green-300 focus:border-green-500 focus:ring-green-500":
                              !errors.phone && touchedFields.phone,
                          }
                        )}
                        placeholder={hostedApplyTx.phonePlaceholder}
                        name="phone"
                      />
                    );
                  }}
                />
                {errors.phone && (
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <ExclamationCircleIcon
                      className="h-5 w-5 text-red-500"
                      aria-hidden="true"
                    />
                  </div>
                )}
                {!errors.phone && touchedFields.phone && (
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <CheckCircleIcon
                      className="h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                  </div>
                )}
              </div>
              {errors.phone?.type === "required" && (
                <p className="mt-2 text-sm text-red-600">
                  {hostedApplyTx.required}
                </p>
              )}
              {errors.phone?.type === "validate" && (
                <p className="mt-2 text-sm text-red-600">
                  {hostedApplyTx.invalid}
                </p>
              )}
            </div>
            <JornayaScript />
            {error && (
              <div role="alert" className="alert alert-error col-span-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 shrink-0 stroke-current"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{hostedApplyTx.failed}</span>
              </div>
            )}
          </form>
        </ModalBody>
        <ModalFooter
          className="sticky bottom-0 flex flex-col gap-4 rounded-b-md bg-primary-600 pt-4 mt-4"
          style={
            {
              "--tw-gradient-to": "#fff 2rem",
            } as CSSProperties
          }
        >
          <ButtonSecondary
            text={hostedApplyTx.continue}
            isLoading={coRegMutation.isLoading || appcastMutation.isLoading}
            onClick={handleSubmit(onSubmit)} // <form>s on Path are unexpectedly submitted by this!
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export const fragments = graphql`
  fragment HostedApply on Translations {
    title
    continue
    firstName
    firstNameInvalid
    lastName
    emailPlaceholder
    emailLabel
    emailInvalid
    phonePlaceholder
    phoneNumberLabel
    phoneNumberLabelInvalid
    required
    invalid
    failed
  }
`;
